import { useNavigate } from 'react-router-dom';
import type { ChargeDetail, ChargeOneTimeDiscountItem } from '../../services/billing';
import { formatMoneyCents, formatMoneyDollars as money } from '../../utils/money';
import { creditUses, type CreditUse } from './creditAllocation';

function fmtDate(iso: string): string {
    return new Date(iso).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    });
}

function shortRef(id: string): string {
    return id.slice(0, 8).toUpperCase();
}

function creditUseNote(use: CreditUse<ChargeOneTimeDiscountItem>): string | null {
    if (use.returnedCents === 0) return null;
    if (use.usedCents === 0) return 'Not needed, unlinked at processing';
    return `${formatMoneyCents(use.returnedCents)} remainder kept as a new credit at processing`;
}

export function AppliedOneTimeDiscounts({ charge }: { charge: ChargeDetail }) {
    const navigate = useNavigate();

    if (charge.oneTimeDiscounts.length === 0) {
        return <p className="text-sm text-gray-400">No one-time discounts applied.</p>;
    }
    return (
        <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
            {creditUses(charge).map((use) => {
                const discount = use.credit;
                const note = charge.status === 'pending' ? creditUseNote(use) : null;
                return (
                    <div key={discount.id} className="flex items-center gap-3 px-4 py-2.5">
                        <div className="min-w-0 flex-1">
                            <p className="text-sm text-gray-600 truncate">{discount.description}</p>
                            <p className="text-[11px] text-gray-400">
                                {discount.sourceChargeId ? (
                                    <button
                                        type="button"
                                        onClick={() =>
                                            navigate(`/billing/charges/${discount.sourceChargeId}`)
                                        }
                                        title={discount.sourceCharge?.description ?? undefined}
                                        className="underline decoration-gray-300 underline-offset-2 hover:text-gray-600 transition-colors"
                                    >
                                        from charge #{shortRef(discount.sourceChargeId)}
                                    </button>
                                ) : (
                                    'manual one-time discount'
                                )}
                                {discount.expiresAt && (
                                    <span> · expires {fmtDate(discount.expiresAt)}</span>
                                )}
                            </p>
                            {note && <p className="text-[11px] text-gray-500">{note}</p>}
                        </div>
                        <span className="text-sm font-semibold text-red-600 tabular-nums ml-auto shrink-0">
                            −{money(discount.amount)}
                        </span>
                    </div>
                );
            })}
        </div>
    );
}
